import React, { memo, useEffect, useMemo, useState } from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
  type MouseHandlerDataParam,
} from 'recharts';
import {
  chartClickRowIndex,
  chartColumnLabelFromBarClick,
} from '../utils/chartTableFilters';
import { signedInOutChartDisplayRows } from '../utils/annualSummaryCharts';

export type AnnualSignedChartRow = {
  label: string;
  In: number;
  Out: number;
  inCount: number;
  outCount: number;
};

type ChartView = 'category' | 'supplier';

const DENSE_COLUMN_MIN_COUNT = 8;
const SPARSE_COLUMN_X_AXIS_HEIGHT = 40;
const SPARSE_COLUMN_CHART_MARGIN = { top: 36, right: 12, left: 0, bottom: 8 } as const;
const DENSE_COLUMN_CHART_MARGIN = { top: 80, right: 12, left: 0, bottom: 8 } as const;

function formatMoneyWhole(amount?: number | null, currency = 'EUR'): string {
  const value = Math.round(Number(amount || 0));
  const ccy = String(currency || 'EUR').trim().toUpperCase() || 'EUR';
  try {
    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: ccy,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${value} ${ccy}`;
  }
}

function formatChartTickWhole(v: number): string {
  const n = Math.round(v);
  return Math.abs(n) >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
}

function isDenseColumnChart(columnCount: number): boolean {
  return columnCount >= DENSE_COLUMN_MIN_COUNT;
}

function denseColumnXAxisHeight(labels: Array<string | number | undefined>): number {
  const maxLen = Math.max(4, ...labels.map((label) => String(label ?? '').length));
  return Math.max(64, Math.min(110, 14 + maxLen * 5.5));
}

function columnChartAxisHeight(labels: Array<string | number | undefined>, dense: boolean): number {
  return dense ? denseColumnXAxisHeight(labels) : SPARSE_COLUMN_X_AXIS_HEIGHT;
}

function columnChartMargin(dense: boolean): { top: number; right: number; left: number; bottom: number } {
  return dense ? DENSE_COLUMN_CHART_MARGIN : SPARSE_COLUMN_CHART_MARGIN;
}

function columnChartHeight(axisHeight: number, dense: boolean, plotHeight = 220): number {
  const margin = columnChartMargin(dense);
  return margin.top + margin.bottom + axisHeight + plotHeight;
}

type DenseColumnXAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value?: string | number };
};

function DenseColumnXAxisTick({ x = 0, y = 0, payload }: DenseColumnXAxisTickProps) {
  const label = String(payload?.value ?? '');
  return (
    <g transform={`translate(${x},${y})`} style={{ pointerEvents: 'none' }}>
      <text
        transform="rotate(-90)"
        textAnchor="end"
        fill="#475569"
        fontSize={10}
        x={-6}
        y={3}
        pointerEvents="none"
      >
        <title>{label}</title>
        {label}
      </text>
    </g>
  );
}

function CategoryColumnXAxis({
  dataKey,
  labels,
  dense,
}: {
  dataKey: string;
  labels: Array<string | number | undefined>;
  dense: boolean;
}) {
  if (dense) {
    return (
      <XAxis
        dataKey={dataKey}
        interval={0}
        minTickGap={0}
        height={denseColumnXAxisHeight(labels)}
        tick={DenseColumnXAxisTick}
        tickLine={false}
      />
    );
  }
  return (
    <XAxis dataKey={dataKey} interval={0} height={SPARSE_COLUMN_X_AXIS_HEIGHT} tick={{ fontSize: 12 }} />
  );
}

function OutsideColumnValueLabel({
  value,
  viewBox,
  x,
  y,
  width,
  height,
  dense,
  format,
  hideZero = true,
}: {
  value?: number | string | Array<number | string>;
  viewBox?: { x?: number; y?: number; width?: number; height?: number };
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  dense: boolean;
  format: (n: number) => string;
  hideZero?: boolean;
}) {
  const raw = Array.isArray(value) ? value[0] : value;
  const n = Number(raw);
  if (!Number.isFinite(n) || (hideZero && Math.round(n) === 0)) return null;
  const boxX = Number(viewBox?.x ?? x);
  const boxY = Number(viewBox?.y ?? y);
  const boxW = Number(viewBox?.width ?? width);
  const boxH = Number(viewBox?.height ?? height);
  if (![boxX, boxY, boxW, boxH].every((v) => Number.isFinite(v))) return null;
  const cx = boxX + boxW / 2;
  const top = boxH >= 0 ? boxY : boxY + boxH;
  const bottom = boxH >= 0 ? boxY + boxH : boxY;
  const isNegative = n < 0 || boxH < 0;
  const gap = 8;
  const tipY = isNegative ? bottom + gap : top - gap;
  const label = format(n);
  if (!dense) {
    return (
      <text
        x={cx}
        y={tipY}
        textAnchor="middle"
        dominantBaseline={isNegative ? 'hanging' : 'auto'}
        fill="#334155"
        fontSize={11}
        fontWeight={600}
        pointerEvents="none"
      >
        {label}
      </text>
    );
  }
  return (
    <text
      x={cx}
      y={tipY}
      transform={`rotate(-90 ${cx} ${tipY})`}
      textAnchor={isNegative ? 'end' : 'start'}
      dominantBaseline="middle"
      fill="#334155"
      fontSize={10}
      fontWeight={600}
      pointerEvents="none"
    >
      {label}
    </text>
  );
}

type AnnualSignedInOutChartProps = {
  title: string;
  rows: AnnualSignedChartRow[];
  onLabelClick?: (label: string) => void;
  activeLabel?: string | null;
  minHeight?: number;
  currency?: string;
  /** Keep columns with both In and Out at zero (e.g. all three families). */
  keepZeroColumns?: boolean;
};

export const AnnualSignedInOutChart = memo(function AnnualSignedInOutChart({
  title,
  rows,
  onLabelClick,
  activeLabel,
  minHeight = 284,
  currency = 'EUR',
  keepZeroColumns = false,
}: AnnualSignedInOutChartProps) {
  const displayRows = useMemo(
    () =>
      keepZeroColumns
        ? rows.map((row) => ({
            ...row,
            In: row.In === 0 ? null : row.In,
            Out: row.Out === 0 ? null : row.Out,
          }))
        : signedInOutChartDisplayRows(rows),
    [rows, keepZeroColumns]
  );
  const formatMoney = (n: number) => formatMoneyWhole(n, currency);
  if (displayRows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No chart data for this section.
      </Typography>
    );
  }
  const labels = displayRows.map((row) => row.label);
  const dense = isDenseColumnChart(rows.length);
  const axisHeight = columnChartAxisHeight(labels, dense);
  const chartHeight = columnChartHeight(axisHeight, dense, minHeight);
  const moneyTip = (value: number | string, name: string) => [
    typeof value === 'number' ? formatMoney(value) : value,
    name,
  ];
  const opacityFor = (label: string) => {
    if (!activeLabel) return 1;
    return activeLabel === label ? 1 : 0.32;
  };
  const handleColumnClick = (
    item: Parameters<typeof chartColumnLabelFromBarClick>[1],
    index: number | undefined,
    activeLabelHint?: string | null
  ) => {
    if (!onLabelClick) return;
    const label = chartColumnLabelFromBarClick(displayRows, item, index, activeLabelHint);
    if (label) onLabelClick(label);
  };

  const handleBarClick = (
    item: Parameters<typeof chartColumnLabelFromBarClick>[1],
    index: number,
    event: React.MouseEvent<SVGPathElement>
  ) => {
    event.stopPropagation();
    const hint = item?.payload?.label;
    handleColumnClick(item, index, hint != null ? String(hint) : undefined);
  };

  /** Column-band clicks (not on a bar rect) — bar clicks stopPropagation so we never double-toggle. */
  const handleChartClick = (state: MouseHandlerDataParam) => {
    const activeLabel =
      state.activeLabel != null ? String(state.activeLabel) : undefined;
    handleColumnClick(undefined, chartClickRowIndex(state), activeLabel);
  };

  return (
    <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {title}
      </Typography>
      <Box
        sx={{
          width: '100%',
          height: chartHeight,
          cursor: onLabelClick ? 'pointer' : 'default',
          '& svg': { outline: 'none' },
          '& .recharts-wrapper': { outline: 'none' },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={displayRows}
            margin={columnChartMargin(dense)}
            style={{ outline: 'none' }}
            onClick={onLabelClick ? handleChartClick : undefined}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <CategoryColumnXAxis dataKey="label" labels={labels} dense={dense} />
            <YAxis width={52} tickFormatter={formatChartTickWhole} />
            <RechartsTooltip
              formatter={moneyTip}
              labelFormatter={(label, payload) => {
                const row = payload?.[0]?.payload as { inCount?: number; outCount?: number } | undefined;
                const parts = [String(label)];
                const inCount = Number(row?.inCount);
                const outCount = Number(row?.outCount);
                if (Number.isFinite(inCount) && inCount > 0) parts.push(`${inCount} in`);
                if (Number.isFinite(outCount) && outCount > 0) parts.push(`${outCount} out`);
                return parts.join(' · ');
              }}
            />
            <Legend verticalAlign="top" height={24} wrapperStyle={{ pointerEvents: 'none' }} />
            <ReferenceLine y={0} stroke="#94A3B8" />
            <Bar
              dataKey="In"
              name="In"
              fill="#16A34A"
              isAnimationActive={false}
              cursor={onLabelClick ? 'pointer' : 'default'}
              onClick={handleBarClick}
            >
              {displayRows.map((row) => (
                <Cell key={`in-${row.label}`} fill="#16A34A" fillOpacity={opacityFor(row.label)} />
              ))}
              <LabelList
                dataKey="In"
                content={(props) => (
                  <OutsideColumnValueLabel
                    value={props.value}
                    viewBox={props.viewBox as { x?: number; y?: number; width?: number; height?: number }}
                    dense={dense}
                    format={formatMoney}
                  />
                )}
              />
            </Bar>
            <Bar
              dataKey="Out"
              name="Out"
              fill="#DC2626"
              isAnimationActive={false}
              cursor={onLabelClick ? 'pointer' : 'default'}
              onClick={handleBarClick}
            >
              {displayRows.map((row) => (
                <Cell key={`out-${row.label}`} fill="#DC2626" fillOpacity={opacityFor(row.label)} />
              ))}
              <LabelList
                dataKey="Out"
                content={(props) => (
                  <OutsideColumnValueLabel
                    value={props.value}
                    viewBox={props.viewBox as { x?: number; y?: number; width?: number; height?: number }}
                    dense={dense}
                    format={formatMoney}
                  />
                )}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
});

export type AnnualSectionSignedChartPanelProps = {
  sectionTitle: string;
  displayCurrency?: string;
  categoryRows: AnnualSignedChartRow[];
  supplierRows: AnnualSignedChartRow[];
  activeSupplierFilter: string | null;
  onSupplierClick: (label: string) => void;
  onCategoryClick?: (label: string) => void;
  activeCategoryFilter?: string | null;
};

/** One chart at a time; inactive chart uses display:none so it cannot steal clicks. */
export const AnnualSectionSignedChartPanel = memo(function AnnualSectionSignedChartPanel({
  sectionTitle,
  displayCurrency = 'EUR',
  categoryRows,
  supplierRows,
  activeSupplierFilter,
  onSupplierClick,
  onCategoryClick,
  activeCategoryFilter = null,
}: AnnualSectionSignedChartPanelProps) {
  const [chartView, setChartView] = useState<ChartView>(
    activeSupplierFilter != null ? 'supplier' : 'category'
  );

  useEffect(() => {
    if (activeSupplierFilter != null) {
      setChartView('supplier');
    } else if (activeCategoryFilter != null) {
      setChartView('supplier');
      setSupplierChartMounted(true);
    }
  }, [activeSupplierFilter, activeCategoryFilter]);

  const showCategory = chartView === 'category';
  const showSupplier = chartView === 'supplier';
  const [supplierChartMounted, setSupplierChartMounted] = useState(
    activeSupplierFilter != null || chartView === 'supplier'
  );

  useEffect(() => {
    if (showSupplier) setSupplierChartMounted(true);
  }, [showSupplier]);

  const activeRows = showSupplier ? supplierRows : categoryRows;
  const chartMinHeight = useMemo(() => {
    const dense = isDenseColumnChart(Math.max(activeRows.length, 1));
    const labels = activeRows.map((row) => row.label);
    const axisHeight = columnChartAxisHeight(labels, dense);
    return columnChartHeight(axisHeight, dense, 284) + 56;
  }, [activeRows]);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {sectionTitle}
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={chartView}
          aria-label={`${sectionTitle} chart breakdown`}
          onChange={(_, value: ChartView | null) => {
            if (!value) return;
            setChartView(value);
          }}
        >
          <ToggleButton value="category" aria-label="By category">
            By category
          </ToggleButton>
          <ToggleButton value="supplier" aria-label="By supplier">
            By supplier
          </ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Box sx={{ width: '100%', minHeight: chartMinHeight }}>
        {showCategory ? (
          <AnnualSignedInOutChart
            title={`${sectionTitle} by category (${displayCurrency})`}
            rows={categoryRows}
            currency={displayCurrency}
            onLabelClick={onCategoryClick}
            activeLabel={activeCategoryFilter}
          />
        ) : supplierChartMounted ? (
          <AnnualSignedInOutChart
            title={`${sectionTitle} by supplier (${displayCurrency})`}
            rows={supplierRows}
            currency={displayCurrency}
            onLabelClick={onSupplierClick}
            activeLabel={activeSupplierFilter}
          />
        ) : null}
      </Box>
    </Box>
  );
});
